import type React from 'react';
import { useEffect } from 'react';

import { isQuestionnaireEnabled, isQuestionnaireWriteEnabled } from '@/feature/questionnaire.feature';
import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import { ModalFooter } from '@ingka/modal';
import warningTriangleIcon from '@ingka/ssr-icon/paths/warning-triangle';
import {
    selectAssetStatus,
    selectOrderNoSource,
    selectOrderType,
} from '@repo/api-adapters/selectors/customer-assets.selectors';
import { useOrderQuestionnaires } from '@repo/hooks';
import { translate } from '@repo/phrase';
import { buildQuestionnaireFormConfig } from '@repo/questionnaire';
import { QuestionnaireFormContent } from '@repo/questionnaire/react';

import { QueryError } from '@/components/query-error';

import { useAsset, useAssetParams } from '@/hooks/asset.hooks';

import styles from './styles.module.scss';

interface Props {
    readonly setFooter: (footer: React.ReactElement | null) => void;
}

const AssetQuestionnaireModalContent = ({ setFooter }: Props) => {
    const { assetId, assetType } = useAssetParams();
    const { data, isError, error } = useAsset(assetType, assetId);
    const orderNoSource = selectOrderNoSource(data);
    const hasOrderNoSource = typeof orderNoSource === 'string' && Boolean(orderNoSource);
    const orderType = selectOrderType(data);
    const assetStatus = selectAssetStatus(data);
    const questionnaireContext = {
        assetType,
        orderType,
        assetStatus,
    };
    const questionnaireEnabled = isQuestionnaireEnabled(questionnaireContext);
    const questionnaireWriteEnabled = isQuestionnaireWriteEnabled(questionnaireContext);
    const orderQuestionnaires = useOrderQuestionnaires(
        {
            orderNoSource: hasOrderNoSource ? orderNoSource : '',
            orderNo: assetId,
        },
        questionnaireEnabled && Boolean(data && hasOrderNoSource)
    );
    const questionnaireTranslations = {
        loading: translate('questionnaire_modal_loading'),
        error: translate('questionnaire_modal_error'),
        empty: translate('questionnaire_modal_empty'),
        deliveryQuestionnaireTitle: translate('questionnaire_modal_delivery_title'),
        serviceQuestionnaireTitle: translate('questionnaire_modal_service_title'),
        questionnaireTypeLabel: translate('questionnaire_modal_type_label'),
        requiredField: translate('questionnaire_modal_required_field'),
        freeTextLabel: translate('questionnaire_modal_free_text_label'),
        freeTextOptional: translate('questionnaire_modal_free_text_optional'),
        freeTextRequired: translate('questionnaire_modal_free_text_required'),
        optionYes: translate('questionnaire_modal_option_yes'),
        optionNo: translate('questionnaire_modal_option_no'),
        optionNotApplicable: translate('questionnaire_modal_option_not_applicable'),
        additionalInfoSp: translate('LBL_QUESTIONNAIRE_ADDITIONAL_INFO_SP'),
        additionalInfoTsp: translate('LBL_QUESTIONNAIRE_ADDITIONAL_INFO_TSP'),
        delAddressInRestrictedTrafficZone: translate('LBL_QUESTIONNAIRE_DEL_ADDRESS_IN_RESTRICTED_TRAFFIC_ZONE'),
        delAddressPartTimeAccessible: translate('LBL_QUESTIONNAIRE_DEL_ADDRESS_PART_TIME_ACCESSIBLE'),
        distanceToParking: translate('LBL_QUESTIONNAIRE_DISTANCE_TO_PARKING'),
        doorCode: translate('LBL_QUESTIONNAIRE_DOOR_CODE'),
        doorWideEnough: translate('LBL_QUESTIONNAIRE_DOOR_WIDE_ENOUGH'),
        electricityAvailable: translate('LBL_QUESTIONNAIRE_ELECTRICITY_AVAILABLE'),
        elevatorExists: translate('LBL_QUESTIONNAIRE_ELEVATOR_EXISTS'),
        externalElevator: translate('LBL_QUESTIONNAIRE_EXTERNAL_ELEVATOR'),
        floorAndWallsFinished: translate('LBL_QUESTIONNAIRE_FLOOR_AND_WALLS_FINISHED'),
        floorNo: translate('LBL_QUESTIONNAIRE_FLOOR_NO'),
        furnitureToBeRemoved: translate('LBL_QUESTIONNAIRE_FURNITURE_TO_BE_REMOVED'),
        islandFerry: translate('LBL_QUESTIONNAIRE_ISLAND_FERRY'),
        kitchenChecklistSigned: translate('LBL_QUESTIONNAIRE_KITCHEN_CHECKLIST_SIGNED'),
        mergedWithOrder: translate('LBL_QUESTIONNAIRE_MERGED_WITH_ORDER'),
        na: translate('LBL_QUESTIONNAIRE_NA'),
        nameOfDoorBell: translate('LBL_QUESTIONNAIRE_NAME_OF_DOOR_BELL'),
        parkingAvailable: translate('LBL_QUESTIONNAIRE_PARKING_AVAILABLE'),
        parkingPermitNeeded: translate('LBL_QUESTIONNAIRE_PARKING_PERMIT_NEEDED'),
        roomPrepared: translate('LBL_QUESTIONNAIRE_ROOM_PREPARED'),
        selectorBusinessCustomer: translate('LBL_QUESTIONNAIRE_SELECTOR_BUSINESS_CUSTOMER'),
        selectorFlat: translate('LBL_QUESTIONNAIRE_SELECTOR_FLAT'),
        selectorHouse: translate('LBL_QUESTIONNAIRE_SELECTOR_HOUSE'),
        stairCaseCurved: translate('LBL_QUESTIONNAIRE_STAIR_CASE_CURVED'),
        stairCaseSize: translate('LBL_QUESTIONNAIRE_STAIR_CASE_SIZE'),
        stairCaseWideEnough: translate('LBL_QUESTIONNAIRE_STAIR_CASE_WIDE_ENOUGH'),
        transportConnected: translate('LBL_QUESTIONNAIRE_TRANSPORT_CONNECTED'),
        truckDeliveryRestrictions: translate('LBL_QUESTIONNAIRE_TRUCK_DELIVERY_RESTRICTIONS'),
        typeOfWhiteGoods: translate('LBL_QUESTIONNAIRE_TYPE_OF_WHITE_GOODS'),
        yes: translate('LBL_QUESTIONNAIRE_YES'),
    };
    const shouldRenderQuestionnaireFooter =
        !orderQuestionnaires.isError &&
        Boolean(
            orderQuestionnaires.data &&
            buildQuestionnaireFormConfig({
                orderQuestionnaires: orderQuestionnaires.data,
                requiredFieldMessage: questionnaireTranslations.requiredField,
                translations: questionnaireTranslations,
            }).elements.length > 0
        );

    useEffect(() => {
        if (!shouldRenderQuestionnaireFooter) {
            setFooter(null);
            return;
        }

        setFooter(
            <ModalFooter className={String(styles.questionnaire_footer)}>
                <Button
                    type="primary"
                    text={translate('questionnaire_modal_save_button')}
                    disabled={!questionnaireWriteEnabled}
                    fluid
                />
                {!questionnaireWriteEnabled && (
                    <InlineMessage
                        className={String(styles.questionnaire_footer_message)}
                        body={translate('questionnaire_modal_readonly_text')}
                        ssrIcon={warningTriangleIcon}
                        variant="informative"
                        subtle
                    />
                )}
            </ModalFooter>
        );

        return () => {
            setFooter(null);
        };
    }, [questionnaireWriteEnabled, setFooter, shouldRenderQuestionnaireFooter]);

    if (isError && !data) {
        return <QueryError error={error} />;
    }

    if (!questionnaireEnabled || !data || !hasOrderNoSource) {
        return null;
    }

    return (
        <QuestionnaireFormContent
            className={styles.questionnaire_form}
            data={orderQuestionnaires.data}
            isLoading={orderQuestionnaires.isLoading}
            isError={orderQuestionnaires.isError}
            params={{
                orderNoSource,
                orderNo: assetId,
            }}
            translations={questionnaireTranslations}
        />
    );
};

export default AssetQuestionnaireModalContent;
